import {
    useAppInsightsContext,
    useTrackEvent,
} from '@microsoft/applicationinsights-react-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Primitive } from 'type-fest';
import { z } from 'zod';

import {
    useForm,
    useFullNameFieldValidation,
    useHotkeys,
    useNameZodSchema,
    useSplitDobZodSchema,
} from '@myphx-engineering/phx-design-system/hooks';
import { PhoneNumberFormatter } from '@phx/utils';

import { type VerifyAccountApiResponse, verifyAccountApi } from '../../api';
import { AccountOperationError } from '../../api/account-operation-error';
import { checkAccountLockoutApi } from '../../api/check-account-lockout.api';
import { sendVerificationCodeApi } from '../../api/send-verification-code.api';
import { type ErrorMessageTypes } from '../../components';
import { CONSTANTS } from '../../constants';
import { defaultBrandId, useBrandContext } from '../../contexts/BrandProvider';
import { useSignupFormFieldProps } from '../../hooks/use-signup-form-field-props';
import { telemetryInstance } from '../../instrumentation';
import type { CreateAccountFormModel } from '../../models/form.models';
import { routes } from '../../routes';
import { getLongestCommonSubString } from '../../utils/get-longest-substring';
import type { SignupChallengeQuestionsPageState } from '../sign-up-challenge-questions/SignUpChallengeQuestionsPage';
import type { VerifyAccountPageState } from '../verify-account/VerifyAccountPage';

type SignUpFormProps = {
    userId: string | undefined;
    callbackUrl: string | undefined;
    prescriberOrderId?: string;
    partnerAccountConfiguration?: {
        initialValues: Record<string, Primitive>;
        schema: Record<string, z.ZodType<Primitive>>;
    };
    savedFormValues?: Record<string, Primitive>;
};

type PendingContinueData = {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    phoneNumber: string;
    unformattedPhoneNumber: string;
    email: string;
    password: string;
    userId: string | undefined;
    configurationValues: Record<string, Primitive>;
};

const shouldSendOtp = (
    response: VerifyAccountApiResponse,
    isBranded: boolean
): boolean => {
    const { success, userId, tryAdditionalInfo = false } = response;

    if (!success) {
        return false;
    }

    if (userId) {
        return true;
    }

    return isBranded || !tryAdditionalInfo;
};

export const useSignUpForm = (props?: SignUpFormProps) => {
    const { brandId } = useBrandContext();
    const isBranded = brandId !== defaultBrandId;
    const {
        userId,
        callbackUrl,
        prescriberOrderId,
        partnerAccountConfiguration,
        savedFormValues,
    } = props ?? {};
    const { minimumPasswordLength } = CONSTANTS;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const nameSchema = useNameZodSchema();
    const { fields: dobFields, superRefine: dobSuperRefine } =
        useSplitDobZodSchema();
    const appInsights = useAppInsightsContext();
    const [loading, setLoading] = useState(false);
    const [errorMessageType, setErrorMessageType] = useState<
        ErrorMessageTypes | undefined
    >(undefined);
    const [lockoutMinutes, setLockoutMinutes] = useState<number | undefined>();
    const [showLockoutError, setShowLockoutError] = useState<boolean>(false);
    const [drxMismatchCount, setDrxMismatchCount] = useState(0);
    const [showDrxModal, setShowDrxModal] = useState(false);
    const [pendingContinueData, setPendingContinueData] = useState<
        PendingContinueData | undefined
    >(undefined);

    const initialFormValues = {
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        memberId: '',
        dobMonth: '',
        dobDay: '',
        dobYear: '',
        ...partnerAccountConfiguration?.initialValues,
        ...savedFormValues,
    };

    const form = useForm<
        CreateAccountFormModel &
            NonNullable<typeof partnerAccountConfiguration>['initialValues']
    >({
        validateInputOnBlur: true,
        initialValues: initialFormValues,
        schema: z
            .object({
                email: z.string().email({
                    message: t('formErrors.formErrorMessage.invalidEmail'),
                }),
                firstName: nameSchema,
                lastName: nameSchema,
                password: z
                    .string({
                        message: t('passwordHelpText.enterPasswordMinChar', {
                            minChar: minimumPasswordLength,
                        }),
                    })
                    .trim()
                    .min(minimumPasswordLength, {
                        message: t('passwordHelpText.enterPasswordMinChar', {
                            minChar: minimumPasswordLength,
                        }),
                    })
                    .regex(/^\S*$/, {
                        message: t('passwordHelpText.containSpaces'),
                    }),
                confirmPassword: z.string(),
                phoneNumber: z.string().regex(/^\(\d{3}\) \d{3}-\d{4}$/, {
                    message: t(
                        'formErrors.formErrorMessage.invalidPhoneNumber'
                    ),
                }),
                memberId: z.string(),
                ...dobFields,
                ...partnerAccountConfiguration?.schema,
            })
            .refine(
                (data) => {
                    const { email, password, phoneNumber } = data;

                    const isSimilarToEmail = email
                        ? getLongestCommonSubString(email, password) >= 4
                        : false;

                    const isSimilarToPhoneNumber = phoneNumber
                        ? getLongestCommonSubString(
                              phoneNumber.replace(/[^\d+]+/g, '').trim(),
                              password
                          ) >= 4
                        : false;

                    return !(isSimilarToEmail || isSimilarToPhoneNumber);
                },
                {
                    message: t('passwordHelpText.similarToEmailOrMobile'),
                    path: ['password'],
                }
            )
            .refine((data) => data.password === data.confirmPassword, {
                message: t('confirmPasswordHelpText.tryAgain'),
                path: ['confirmPassword'],
            })
            .superRefine(dobSuperRefine),
    });

    const trackForSubmit = useTrackEvent(
        appInsights,
        'SIGNUP_CONTINUE_CLICK',
        {}
    );

    const proceedToOtp = async (data: PendingContinueData) => {
        setLoading(true);
        const sendVerificationCodeResponse = await sendVerificationCodeApi(
            data.phoneNumber
        );

        if (sendVerificationCodeResponse.success) {
            trackForSubmit({ message: 'OTP sent successfully' });
            const verifyPageState: VerifyAccountPageState = {
                firstName: data.firstName,
                lastName: data.lastName,
                dateOfBirth: data.dateOfBirth,
                phoneNumber: data.phoneNumber,
                email: data.email,
                password: data.password,
                callBackUrl: callbackUrl ?? '',
                userId: data.userId,
                partnerAccountConfiguration: data.configurationValues,
                isSignupFlow: true,
                unformattedPhoneNumber: data.unformattedPhoneNumber,
            };
            navigate(
                { pathname: routes.verify, search: window.location.search },
                { state: verifyPageState }
            );
            return;
        }

        trackForSubmit({ message: 'Failed to send OTP' });
        setLoading(false);
        setErrorMessageType(sendVerificationCodeResponse.error);
        telemetryInstance.logError({
            source: location.pathname,
            exception: new Error(
                sendVerificationCodeResponse.error ?? 'OTP request failed'
            ),
        });
    };

    const onFormSubmit = async (
        formData: CreateAccountFormModel &
            NonNullable<typeof partnerAccountConfiguration>['initialValues']
    ) => {
        trackForSubmit({ message: 'Registration form is submitted' });
        setErrorMessageType(undefined);
        setShowLockoutError(false);
        setLoading(true);

        const {
            firstName,
            lastName,
            email,
            password,
            phoneNumber,
            dobMonth,
            dobDay,
            dobYear,
            memberId,
        } = formData;

        const formattedPhoneNumber =
            PhoneNumberFormatter.formatForApi(phoneNumber);

        // Check if account is locked before proceeding
        const lockoutStatus =
            await checkAccountLockoutApi(formattedPhoneNumber);

        if (lockoutStatus.isLocked && lockoutStatus.minutesUntilUnlock) {
            setLockoutMinutes(lockoutStatus.minutesUntilUnlock);
            setShowLockoutError(true);
            setLoading(false);
            telemetryInstance.logEvent({
                name: 'SignUpBlockedDueToLockout',
                source: 'SignUpPage',
            });
            return;
        }
        const configKeys = Object.keys(
            partnerAccountConfiguration?.initialValues ?? {}
        );
        const configurationValues = configKeys.reduce(
            (acc, key) => {
                acc[key] = formData[key];
                return acc;
            },
            {} as Record<string, Primitive>
        );

        const trimmedFirstName = firstName?.trim();
        const trimmedLastName = lastName?.trim();

        const dateOfBirth = `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`;

        const verifyAccountResponse = await verifyAccountApi({
            firstName: trimmedFirstName,
            lastName: trimmedLastName,
            email,
            dateOfBirth,
            phoneNumber: formattedPhoneNumber,
            masterId: userId,
            memberId,
            ...(prescriberOrderId && {
                drxId: prescriberOrderId,
                mismatchCount: drxMismatchCount,
            }),
        });

        const {
            userId: userIdFromVerifyResponse,
            error: verifyAccountError,
            tryAdditionalInfo = false,
        } = verifyAccountResponse;

        if (shouldSendOtp(verifyAccountResponse, isBranded)) {
            // Send OTP using old flow
            const sendVerificationCodeResponse =
                await sendVerificationCodeApi(formattedPhoneNumber);

            if (sendVerificationCodeResponse.success) {
                trackForSubmit({ message: 'OTP sent successfully' });
                const verifyPageState: VerifyAccountPageState = {
                    firstName: trimmedFirstName,
                    lastName: trimmedLastName,
                    dateOfBirth,
                    phoneNumber: formattedPhoneNumber,
                    email,
                    password,
                    callBackUrl: callbackUrl ?? '',
                    userId: userId ?? userIdFromVerifyResponse,
                    partnerAccountConfiguration: configurationValues,
                    isSignupFlow: true,
                    unformattedPhoneNumber: phoneNumber,
                };
                navigate(
                    { pathname: routes.verify, search: window.location.search },
                    {
                        state: verifyPageState,
                    }
                );
                return;
            }

            trackForSubmit({ message: 'Failed to send OTP' });
            setLoading(false);
            setErrorMessageType(sendVerificationCodeResponse.error);
            telemetryInstance.logError({
                source: location.pathname,
                exception: new Error(
                    sendVerificationCodeResponse.error ?? 'OTP request failed'
                ),
            });
            return;
        }

        if (tryAdditionalInfo && !isBranded) {
            const challengeQuestionsPageState: SignupChallengeQuestionsPageState =
                {
                    firstName: trimmedFirstName,
                    lastName: trimmedLastName,
                    dateOfBirth,
                    phoneNumber: formattedPhoneNumber,
                    email,
                    password,
                    callBackUrl: callbackUrl ?? '',
                    userId: userId ?? userIdFromVerifyResponse,
                    partnerAccountConfiguration: configurationValues,
                };

            trackForSubmit({
                message: 'Potential match found; prompting for more info',
            });
            navigate(routes.signUpChallengeQuestions, {
                state: challengeQuestionsPageState,
            });

            return;
        }

        if (
            verifyAccountError === '400_MismatchedInformationError' &&
            prescriberOrderId
        ) {
            const currentData: PendingContinueData = {
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                dateOfBirth,
                phoneNumber: formattedPhoneNumber,
                unformattedPhoneNumber: phoneNumber,
                email,
                password,
                userId: userId ?? userIdFromVerifyResponse,
                configurationValues,
            };

            const serverCount =
                verifyAccountResponse.mismatchCount ?? drxMismatchCount + 1;
            setDrxMismatchCount(serverCount);

            if (serverCount <= 2) {
                setPendingContinueData(currentData);
                setShowDrxModal(true);
                setLoading(false);
                return;
            }

            await proceedToOtp(currentData);
            return;
        }

        trackForSubmit({ message: 'Failed to verify account' });
        setLoading(false);
        setErrorMessageType(verifyAccountError);
        telemetryInstance.logError({
            source: location.pathname,
            exception: new AccountOperationError(
                verifyAccountError,
                verifyAccountResponse.errorMessage,
                'Account verification failed'
            ),
        });

        if (verifyAccountError === '409_MultiplePatientsFoundForDemographic') {
            navigate(routes.signUpError);
        }
    };

    const onDrxModalMakeChanges = () => {
        setShowDrxModal(false);
    };

    const onDrxModalContinue = async () => {
        setShowDrxModal(false);
        if (pendingContinueData) {
            await proceedToOtp(pendingContinueData);
        }
    };

    useHotkeys([['Enter', () => form.onSubmit(onFormSubmit)]]);

    useFullNameFieldValidation(form);

    const {
        firstNameProps,
        lastNameProps,
        dobMonthProps,
        dobDayProps,
        dobYearProps,
        onGroupValidation: onDobGroupValidation,
    } = useSignupFormFieldProps(form);

    return {
        form,
        firstNameProps,
        lastNameProps,
        dobMonthProps,
        dobDayProps,
        dobYearProps,
        onDobGroupValidation,
        loading,
        errorMessageType,
        lockoutMinutes,
        showLockoutError,
        onFormSubmit,
        trackForSubmit,
        showDrxModal,
        pendingContinueData,
        onDrxModalMakeChanges,
        onDrxModalContinue,
    };
};
